import { useState, useEffect, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/data/translations';
import SnowCanvas from '@/components/SnowCanvas';
import SEOHead from '@/components/SEOHead';
import { ChevronDown } from 'lucide-react';

const InteractiveMap = lazy(() => import('@/components/InteractiveMap'));

const IMAGES = {
  exterior_summer: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/0nib2e5t_IMMOCONCEPT_RIEME_VAL_BAGNES_EXT_JOUR_MAJ_HD.jpg',
  exterior_winter: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/xjj2xjdt_IMMOCONCEPT_RIEME_VAL_BAGNES_EXT_NUIT_MAJ_HD.jpg',
  exterior_eve: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/xjj2xjdt_IMMOCONCEPT_RIEME_VAL_BAGNES_EXT_NUIT_MAJ_HD.jpg',
  exterior_night: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/xjj2xjdt_IMMOCONCEPT_RIEME_VAL_BAGNES_EXT_NUIT_MAJ_HD.jpg',
  bedroom_close: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/ua0s24yx_IMMOCONCEPT_RIEME_VAL_BAGNES_CHAMBRE_CLOSEUP.jpg',
  bedroom_hd: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/1p1amqsf_IMMOCONCEPT_RIEME_VAL_BAGNES_CHAMBRE_HD.jpg',
  living_hd: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/y0j095xe_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_HD.jpg',
  living_fireplace: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/2hloet6w_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_CLOSUP_A.jpg',
  living_dining: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/fp8zu8z2_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_CLOSUP_B.jpg',
  spa_closeup: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/6kemspid_IMMOCONCEPT_RIEME_VAL_BAGNES_INT_SPA_CLOSUP.jpg',
  spa_sauna: 'https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/cu7rhqpv_IMMOCONCEPT_RIEME_VAL_BAGNES_SPA_HD.jpg',
  mountains: 'https://images.pexels.com/photos/803028/pexels-photo-803028.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  matterhorn: 'https://images.unsplash.com/photo-1680708542215-6a8b7e3924cc?w=1600&q=80',
};

function FadeIn({ children, delay = 0, className = '' }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function HeroSection() {
  const { lang } = useLanguage();
  const hero = t.hero[lang];
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowCta(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const scrollDown = () => {
    const el = document.getElementById('intro-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-obsidian">
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.exterior_eve}
          alt=""
          className="w-full h-full object-cover opacity-60"
        />
      </div>
      <div className="hero-video-wrap">
        <iframe
          src="https://www.youtube.com/embed/YWn12MaUwYU?autoplay=1&mute=1&loop=1&playlist=YWn12MaUwYU&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&playsinline=1&enablejsapi=1"
          title="Chalet Gemini 1308"
          allow="autoplay; encrypted-media"
          allowFullScreen
          data-testid="hero-video"
        />
      </div>

      <div className="absolute inset-0 z-[5]" style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.3) 0%, rgba(10,10,10,0.1) 40%, rgba(10,10,10,0.6) 100%)' }} />

      <SnowCanvas />

      <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 1 }}
          className="text-xs sm:text-sm uppercase tracking-[0.25em] text-alpine-mist/60 font-sans mb-6"
        >
          {hero.overline}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-5xl sm:text-6xl lg:text-7xl text-alpine-mist tracking-tight leading-none"
        >
          {hero.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 2.5 }}
          className="text-sm sm:text-base tracking-[0.15em] text-alpine-mist/50 font-sans mt-4"
        >
          {hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={showCta ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16"
        >
          <button
            onClick={scrollDown}
            data-testid="hero-cta"
            className="cta-luxury"
          >
            {hero.cta}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={showCta ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-10"
        >
          <ChevronDown className="text-alpine-mist/30 animate-bounce" size={20} />
        </motion.div>
      </div>
    </section>
  );
}

function IntroSection() {
  const { lang } = useLanguage();
  const intro = t.intro[lang];

  return (
    <section
      id="intro-section"
      data-testid="intro-section"
      className="relative min-h-screen overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.exterior_night}
          alt="Chalet Gemini 1308 Night"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.7) 50%, rgba(10,10,10,0.9) 100%)' }} />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen text-center px-6 md:px-12">
        <div className="max-w-3xl py-32">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-muted/60 font-sans mb-8">
              {intro.overline}
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-alpine-mist tracking-tight leading-tight">
              {intro.title}
            </h2>
          </FadeIn>
          <FadeIn delay={0.4}>
            <p className="text-base sm:text-lg text-alpine-mist/50 font-sans font-light leading-relaxed mt-10 max-w-2xl mx-auto">
              {intro.text}
            </p>
          </FadeIn>
          <FadeIn delay={0.6}>
            <p className="text-sm text-gold-muted/60 font-sans tracking-[0.1em] mt-8">
              {intro.sub}
            </p>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function VerbierSection() {
  const { lang } = useLanguage();
  const v = t.verbier[lang];

  return (
    <section data-testid="verbier-section" className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={IMAGES.matterhorn}
          alt="Swiss Alps"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.5) 60%, rgba(10,10,10,0.3) 100%)' }} />
      </div>

      <div className="relative z-10 flex items-center min-h-screen px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl py-32">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-muted/70 font-sans mb-8">
              {v.overline}
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-alpine-mist tracking-tight leading-tight">
              {v.title}
            </h2>
          </FadeIn>
          <div className="mt-12 space-y-8">
            {v.paragraphs.map((p, i) => (
              <FadeIn key={i} delay={0.4 + i * 0.2}>
                <p className={`text-base sm:text-lg text-alpine-mist/55 font-sans font-light leading-relaxed tracking-wide ${
                  i === v.paragraphs.length - 1 ? 'text-alpine-mist/70 italic font-serif text-lg sm:text-xl' : ''
                }`}>
                  {p}
                </p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={1.4}>
            <div className="mt-14 pt-8 border-t border-white/8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-muted/50 font-sans mb-3">
                {lang === 'fr' ? 'EMPLACEMENT' : 'LOCATION'}
              </p>
              <p className="text-sm text-alpine-mist/60 font-sans leading-relaxed">
                Chemin de Plampro 32 &mdash; Parcelle 16438
              </p>
              <p className="text-sm text-alpine-mist/50 font-sans mt-1">
                Médières - Verbier - 1936 &bull; Commune de Val de Bagnes &bull; Valais &bull; Suisse
              </p>
              <div className="mt-6">
                <Suspense fallback={<div className="h-[200px] bg-obsidian/50 animate-pulse rounded-sm" />}>
                  <InteractiveMap height="200px" variant="dark" />
                </Suspense>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function ArchitectureSection() {
  const { lang } = useLanguage();
  const arch = t.architecture[lang];

  return (
    <section data-testid="architecture-section" className="section-light py-24 md:py-32 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.25em] text-warm-wood font-sans mb-4">{arch.overline}</p>
        </FadeIn>
        <FadeIn delay={0.15}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal tracking-tight leading-tight max-w-2xl">
            {arch.title}
          </h2>
        </FadeIn>
        <FadeIn delay={0.25}>
          <p className="text-base text-charcoal/50 font-sans font-light leading-relaxed mt-6 max-w-2xl">
            {arch.subtitle}
          </p>
        </FadeIn>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <FadeIn delay={0.3}>
            <div className="overflow-hidden">
              <img
                src={IMAGES.living_hd}
                alt="Living Room"
                className="w-full h-[400px] lg:h-[500px] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                loading="lazy"
              />
            </div>
          </FadeIn>
          <div>
            <FadeIn delay={0.35}>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-serif text-4xl sm:text-5xl text-charcoal tracking-tight">{arch.price}</span>
              </div>
              <p className="text-sm text-warm-wood font-sans tracking-[0.1em] uppercase mb-8">{arch.lots}</p>
            </FadeIn>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {arch.specs.map((s, i) => (
                <FadeIn key={i} delay={0.4 + i * 0.08}>
                  <div className="border-b border-charcoal/10 pb-3">
                    <p className="text-[0.65rem] uppercase tracking-[0.15em] text-charcoal/35 font-sans">{s.label}</p>
                    <p className="text-sm text-charcoal/80 font-sans mt-1">{s.value}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <FadeIn delay={0.3}>
            <div className="overflow-hidden">
              <img
                src={IMAGES.living_fireplace}
                alt="Fireplace"
                className="w-full h-[300px] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                loading="lazy"
              />
            </div>
          </FadeIn>
          <FadeIn delay={0.4}>
            <div className="overflow-hidden">
              <img
                src={IMAGES.bedroom_hd}
                alt="Master Suite"
                className="w-full h-[300px] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                loading="lazy"
              />
            </div>
          </FadeIn>
          <FadeIn delay={0.5}>
            <div className="overflow-hidden">
              <img
                src={IMAGES.living_dining}
                alt="Dining"
                className="w-full h-[300px] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                loading="lazy"
              />
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.3}>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-4">
            {arch.features.map((f, i) => (
              <FadeIn key={i} delay={0.1 * i}>
                <p className="text-sm text-charcoal/55 font-sans font-light tracking-wide py-2 border-b border-charcoal/8">
                  {f}
                </p>
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function LifestyleSection() {
  const { lang } = useLanguage();
  const ls = t.lifestyle[lang];

  return (
    <section data-testid="lifestyle-section" className="section-dark py-24 md:py-32 lg:py-40">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.25em] text-gold-muted/50 font-sans mb-8">{ls.overline}</p>
        </FadeIn>
        <FadeIn delay={0.2}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-alpine-mist tracking-tight leading-tight">
            {ls.title}
          </h2>
        </FadeIn>
        <FadeIn delay={0.35}>
          <p className="text-base text-alpine-mist/40 font-sans font-light leading-relaxed mt-6 max-w-2xl mx-auto">
            {ls.text}
          </p>
        </FadeIn>
        <FadeIn delay={0.45}>
          <p className="text-sm text-gold-muted/40 font-sans italic mt-4">
            {ls.references}
          </p>
        </FadeIn>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 text-left">
          {ls.pillars.map((p, i) => (
            <FadeIn key={i} delay={0.5 + i * 0.12}>
              <div className="border-t border-white/8 pt-6">
                <h3 className="text-sm font-sans text-alpine-mist/70 tracking-wide mb-3">{p.title}</h3>
                <p className="text-xs text-alpine-mist/35 font-sans font-light leading-relaxed">{p.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function WellnessSection() {
  const { lang } = useLanguage();
  const well = t.wellness[lang];

  return (
    <section data-testid="wellness-section" className="section-forest relative min-h-screen overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={IMAGES.spa_sauna}
          alt="Wellness Spa"
          className="w-full h-full object-cover opacity-50"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-forest-deep/60" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen text-center px-6 md:px-12">
        <div className="max-w-2xl py-32">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-muted/60 font-sans mb-8">{well.overline}</p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-alpine-mist tracking-tight leading-tight italic">
              {well.title}
            </h2>
          </FadeIn>
          <div className="mt-12 space-y-4">
            {well.features.map((f, i) => (
              <FadeIn key={i} delay={0.5 + i * 0.2}>
                <p className="text-base sm:text-lg text-alpine-mist/50 font-sans font-light tracking-wide">
                  {f}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function InvestorSection() {
  const { lang } = useLanguage();
  const inv = t.investor[lang];

  return (
    <section data-testid="investor-section" className="bg-stone-beige py-24 md:py-32 lg:py-40">
      <div className="max-w-3xl mx-auto text-center px-6 md:px-12">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.25em] text-warm-wood font-sans mb-6">{inv.overline}</p>
        </FadeIn>
        <FadeIn delay={0.15}>
          <h2 className="font-serif text-3xl sm:text-4xl text-obsidian tracking-tight mb-14">{inv.title}</h2>
        </FadeIn>
        <div className="space-y-5">
          {inv.items.map((item, i) => (
            <FadeIn key={i} delay={0.2 + i * 0.12}>
              <p className="text-base sm:text-lg text-charcoal font-sans font-light tracking-wide">{item}</p>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.7}>
          <p className="text-sm text-warm-wood font-sans font-light leading-relaxed mt-12 max-w-xl mx-auto">
            {inv.text}
          </p>
        </FadeIn>
        <div className="mt-20 border-t border-charcoal/10 pt-16">
          <FadeIn delay={0.85}>
            <p className="font-serif text-2xl sm:text-3xl text-charcoal tracking-tight">{inv.closing}</p>
          </FadeIn>
          <FadeIn delay={1}>
            <p className="font-serif text-3xl sm:text-4xl text-obsidian tracking-tight mt-3 italic">{inv.closingSub}</p>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function ClosingSection({ onBrochureClick }) {
  const { lang } = useLanguage();
  const cl = t.closing[lang];

  return (
    <section data-testid="closing-section" className="section-dark min-h-screen flex items-center justify-center">
      <div className="text-center px-6 md:px-12 py-32">
        <FadeIn>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-alpine-mist tracking-tight leading-tight">
            {cl.title}
          </h2>
        </FadeIn>
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6">
          <FadeIn delay={0.4}>
            <button onClick={onBrochureClick} data-testid="closing-brochure-btn" className="cta-luxury">
              {cl.ctaBrochure}
            </button>
          </FadeIn>
          <FadeIn delay={0.6}>
            <a href="/contact" data-testid="closing-viewing-btn" className="cta-luxury">
              {cl.ctaViewing}
            </a>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

export default function HomePage({ onBrochureClick }) {
  return (
    <main data-testid="home-page">
      <SEOHead page="home" />
      <HeroSection />
      <IntroSection />
      <VerbierSection />
      <ArchitectureSection />
      <LifestyleSection />
      <WellnessSection />
      <InvestorSection />
      <ClosingSection onBrochureClick={onBrochureClick} />
    </main>
  );
}
